import { cloneDeep, debounce, isEqual, pick } from 'lodash';
import { StoreApi } from 'zustand';
import { CustomizationStoreState } from '../stores/customizationStore';

/**
 * Fields that should trigger dirty state when changed.
 * These are the core store configuration and content fields that users can modify.
 */
export const RELEVANT_FIELDS_FOR_DIRTY = [
    'theme',
    'primaryFont',
    'secondaryFont',
    'colors',
    'imageFrames',
    'header',
    'sections',
    'allPages',
    'storeItems',
] as const;

/**
 * Creates a snapshot of the current state containing only fields relevant for dirty checking.
 * @param state - The current state to snapshot
 * @returns A partial state object with only relevant fields
 */
export const createCachedStateSnapshot = (
    state: Partial<CustomizationStoreState>
): Partial<CustomizationStoreState> => {
    // Deep clone to avoid shared references between cachedState and live state
    // which can cause false positives/negatives in deep equality checks
    return cloneDeep(pick(state, RELEVANT_FIELDS_FOR_DIRTY));
};

/**
 * Helper function to calculate isDirty by comparing relevant state fields
 * @param currentState - The current state
 * @param cachedState - The cached state to compare against
 * @returns true if the state is dirty (has changes), false otherwise
 */
export const calculateIsDirty = (
    currentState: CustomizationStoreState,
    cachedState: Partial<CustomizationStoreState> | null
): boolean => {
    if (!cachedState) return false;

    const cleanCurrentState = pick(currentState, RELEVANT_FIELDS_FOR_DIRTY);
    const cleanCachedState = pick(cachedState, RELEVANT_FIELDS_FOR_DIRTY);

    return !isEqual(cleanCachedState, cleanCurrentState);
};

/**
 * Sets up automatic isDirty calculation with debouncing for a Zustand store.
 * This subscribes to state changes and automatically updates the isDirty flag.
 * @param store - The Zustand store API
 * @returns Cleanup function to cancel pending debounced calls and unsubscribe
 */
export const setupIsDirtySubscription = (store: StoreApi<CustomizationStoreState>): (() => void) => {
    const debouncedUpdateIsDirty = debounce(() => {
        const state = store.getState();
        const isDirty = calculateIsDirty(state, state.cachedState);

        if (state.isDirty !== isDirty) {
            store.setState({ isDirty });
        }
    }, 300);

    // Subscribe to any state changes to automatically update isDirty
    const unsubscribe = store.subscribe(state => {
        if (state.cachedState !== null) debouncedUpdateIsDirty();
    });

    // Return cleanup function that cancels pending debounced calls and unsubscribes
    return () => {
        debouncedUpdateIsDirty.cancel();
        unsubscribe();
    };
};
